import querystring from 'querystring';
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import loadable from '@loadable/component';
import styled from 'styled-components';
import history from 'history/browser';
import { addHours, isWithinInterval, subMinutes } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import coreUtils from '@opentripplanner/core-utils';
import useTripOptions from '#/trip-planner/hooks/useTripOptions';
import useCurrentQuery from '#/trip-planner/hooks/useCurrentQuery';
import TripOptionsTabs from '#/trip-planner/components/TripOptionsTabs';
import TripModeSummary from '#/trip-planner/components/TripModeSummary';
import useActiveItinerary from '#/trip-planner/hooks/useActiveItinerary';
import useNotification from '#/shared/hooks/useNotification';
import useClipboardCopy from '#/shared/hooks/useClipboardCopy';
import TripHopCardFooter from '#/trip-planner/components/TripHopCardFooter';
import DetailViewLayout from '#/components/DetailViewLayout';
import DetailNavHeader from '#/shared/components/DetailNavHeader';
import SEO from '#/shared/components/SEO';
import TripPlanError from '#/trip-planner/components/TripPlanError';
import useNavigate from '#/shared/hooks/useNavigate';
import ROUTES from '#/constants/routes';
import NotificationOverlay from '#/shared/components/NotificationOverlay';
import useViewMapControls from '#/map/hooks/useViewMapControls';
import usePrintTrip from '#/trip-planner/hooks/usePrintTrip';
import { useGetArrivalsQuery } from '#/services/developerApi';
import { parseOtpId } from '#/trip-planner/utils/itinerary';
import {
  DESKTOP_MIN_BREAK,
  DESKTOP_MIN_BREAK_NUMBER,
  ARRIVALS_REFRESH_INTERVAL,
} from '#/shared/constants';
import TripPlannerContainerSkeletons from '#/shared/components/Skeletons/TripPlannerContainerSkeletons';
import useEstimatedArrivalText from '#/transit-tracker/hooks/useEstimatedArrivalText';
import useTriMetStaticPageLinks from '#/shared/hooks/useTriMetStaticPageLinks';
import otpUiConfig from '#/otp-ui-config';

// this component is lazy loaded due to the large file size of its dependencies
const Itinerary = loadable(() => import('#/trip-planner/components/Itinerary'));

const TripContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    overflow-y: auto;
  }
  /* this is used to set the scrollbar color, as the background itself
  is covered by child elements */
  background-color: ${props => props.theme.backgroundColor};
`;

function TripPlanContainer() {
  const tripContentContainerEl = useRef(null);
  const { navigate, generateNavigateBack, generateNavigateClose } =
    useNavigate();
  const { plan, planError, refreshTripOptions, clearTripOptions } =
    useTripOptions();
  const {
    activeItinerary,
    setActiveItinerary,
    itineraryLegFocused,
    itineraryLegCleared,
  } = useActiveItinerary();
  const { currentQuery } = useCurrentQuery();
  const { notificationVisible, popNotification } = useNotification();
  const copyTextToClipboard = useClipboardCopy();
  const { tripPlanViewOpened, viewClosed } = useViewMapControls();

  const { activeItineraryIsTransit } = useSelector(s => s.tripPlanner);
  const intl = useIntl();
  const { printTrip } = usePrintTrip();

  const { trimetLinks } = useTriMetStaticPageLinks();

  const navigateToTermsOfUse = () => {
    window.open(trimetLinks.termsOfUse, '_blank');
  };
  const navigateToPrivacyPolicy = () => {
    window.open(trimetLinks.privacyPolicy, '_blank');
  };
  const navigateToPaymentOptions = () => {
    navigate(ROUTES.pay);
  };

  const navigateTripPlanner = () => {
    navigate(ROUTES.planner);
  };
  const navigateRiderFeedback = () => {
    navigate(ROUTES.riderFeedback);
  };
  const navigateStop = stopId => {
    navigate(ROUTES.stopById, stopId);
  };

  useEffect(() => {
    return itineraryLegCleared;
  }, [itineraryLegCleared]);

  const frameLeg = ({ legIndex }) => {
    if (window.innerWidth < DESKTOP_MIN_BREAK_NUMBER) {
      /*  
        scrollToOptions is not compatible with all browsers yet 
        We should be prepared to handle it gracefully in any 
        incompatible environment
      */
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
    }
    itineraryLegFocused(legIndex);
  };

  // TODO: replace direct reading and writing of querystring parameters
  //  with use of the router api once the router is upgraded to a version
  //  that supports these actions

  const [loadTripOptionFromQuerystring, setLoadTripOptionFromQuerystring] =
    useState(true);

  // if a trip option is present in the initial url for this view set
  // the active itinerary to correspond to that querystring trip option
  useEffect(() => {
    const querystringTripOption = Number(
      querystring.parse(window.location.search).tripOption
    );

    if (
      loadTripOptionFromQuerystring &&
      activeItinerary &&
      querystringTripOption
    ) {
      if (activeItinerary.id !== querystringTripOption) {
        setActiveItinerary(querystringTripOption);
      }

      // only the initial querystring trip option is loaded
      setLoadTripOptionFromQuerystring(false);
    }
  }, [activeItinerary, loadTripOptionFromQuerystring, setActiveItinerary]);

  const [tripUrl, setTripUrl] = useState(window.location.href);

  // update the `tripOption` querystring param when the trip itinerary
  // option is changed
  useEffect(() => {
    if (activeItinerary) {
      const nextTripUrl = `${window.location.origin}${
        window.location.pathname
      }?${querystring.encode({
        ...coreUtils.query.getRoutingParams(otpUiConfig, currentQuery),
        tripOption: activeItinerary.id,
      })}`;

      if (tripUrl !== nextTripUrl) {
        history.replace(nextTripUrl);
        setTripUrl(nextTripUrl);

        // ensure that the querystring trip option isn't loaded when its
        // value has been set in the current app session
        setLoadTripOptionFromQuerystring(false);
      }
    }
  }, [currentQuery, activeItinerary, tripUrl]);

  const onTripOptionChange = useCallback(
    (...args) => {
      setActiveItinerary(...args);

      // when the trip option is changed scroll the trip content container
      // to the top
      tripContentContainerEl.current.scrollTop = 0;
    },
    [setActiveItinerary, tripContentContainerEl]
  );

  const copyTripUrl = useCallback(() => {
    copyTextToClipboard(tripUrl);
    popNotification();
  }, [tripUrl, popNotification, copyTextToClipboard]);

  useEffect(() => {
    tripPlanViewOpened();
    return viewClosed;
  }, [tripPlanViewOpened, viewClosed]);

  const { from, to } = currentQuery;

  // plan the trip on mount and when trip end points are changed via the
  // user dragging them on the map
  useEffect(() => {
    refreshTripOptions();
  }, [refreshTripOptions, from, to]);

  useEffect(() => {
    return clearTripOptions;
  }, [clearTripOptions]);

  // the otp-ui `itinerary-body` component sorts leg alerts, but since
  // this app's itinerary objects are in the redux state they can't be mutated,
  // so create copies of the alerts portion of the itinerary object's tree
  const mutableItinerary = useMemo(
    () =>
      activeItinerary && {
        ...activeItinerary,
        legs: activeItinerary.legs.map(l => ({
          ...l,
          alerts: l.alerts && [...l.alerts],
        })),
      },
    [activeItinerary]
  );

  const skeletonMode = !activeItinerary && !planError;

  let arrivalsQueryParams = {};
  let skipArrivalsQuery = true;
  const initialTransitLeg = activeItinerary?.legs.find(l => l.transitLeg);
  const initialStopId = parseOtpId(initialTransitLeg?.from.stopId);

  if (initialStopId) {
    const now = new Date();
    const itineraryFirstArrival = new Date(initialTransitLeg.from.arrival);

    if (
      // only query for arrivals when the itinerary arrival time is
      // close to the current time
      isWithinInterval(itineraryFirstArrival, {
        start: subMinutes(now, 20),
        end: addHours(now, 1),
      })
    ) {
      skipArrivalsQuery = false;
      arrivalsQueryParams = {
        locIDs: initialStopId,
        begin: subMinutes(itineraryFirstArrival, 5).getTime(),
      };
    }
  }

  const arrivalsResponse = useGetArrivalsQuery(arrivalsQueryParams, {
    skip: skipArrivalsQuery,
    pollingInterval: ARRIVALS_REFRESH_INTERVAL,
  });

  let { data: arrivals } = arrivalsResponse;
  const { error } = arrivalsResponse;

  if (error) {
    arrivals = null;
    console.warn('itinerary arrivals request failed:', error);
  }

  const { getEstimatedArrivalText } = useEstimatedArrivalText();
  const initialTripId = initialTransitLeg?.tripId;
  const initialArrival = arrivals?.find(
    a => a.tripID === parseOtpId(initialTripId)
  );

  const { arrivalText: initialArrivalText } = getEstimatedArrivalText(
    initialArrival,
    { includePredicate: true }
  );

  const navigateToArrivalVehicle = useCallback(() => {
    const { route, blockID } = initialArrival || {};

    if (route && blockID && initialStopId) {
      navigate(ROUTES.vehicleWithSelectedStop, route, blockID, initialStopId);
    }
  }, [initialArrival, initialStopId, navigate]);

  return (
    <>
      <SEO
        metadataId="tripItinerary"
        dynamicValues={{
          origin: currentQuery.from?.name,
          destination: currentQuery.to?.name,
        }}
      />
      <DetailViewLayout
        top={
          <DetailNavHeader
            onBack={generateNavigateBack([], ROUTES.planner)}
            onClose={generateNavigateClose()}
            content={<FormattedMessage id="planner.triptitle" />}
          />
        }
        bottom={
          <>
            {skeletonMode && <TripPlannerContainerSkeletons />}
            {plan && plan.itineraries.length > 0
              ? activeItinerary && (
                  <TripOptionsTabs
                    itineraries={plan.itineraries}
                    activeItinerary={activeItinerary}
                    setActiveItinerary={onTripOptionChange}
                  />
                )
              : planError && <TripPlanError planError={planError} />}
            <TripContentContainer ref={tripContentContainerEl}>
              {activeItinerary && !planError && (
                <>
                  <TripModeSummary
                    itinerary={activeItinerary}
                    toAddress={to.name}
                    fromAddress={from.name}
                    navigateTripPlanner={navigateTripPlanner}
                  />
                  <Itinerary
                    itinerary={mutableItinerary}
                    frameLeg={frameLeg}
                    navigateStop={navigateStop}
                    arrivalText={initialArrivalText}
                    arrivalTripId={initialTripId}
                    navigateToArrivalVehicle={navigateToArrivalVehicle}
                    setActiveLeg={legIndex => frameLeg({ legIndex })}
                  />
                </>
              )}
              <TripHopCardFooter
                paymentOptionHandler={navigateToPaymentOptions}
                copyTripURLHandler={copyTripUrl}
                copyTripNotifyVisible={notificationVisible}
                editTripHandler={navigateTripPlanner}
                printTripHandler={printTrip}
                riderFeedbackHandler={navigateRiderFeedback}
                privacyPolicyHandler={navigateToPrivacyPolicy}
                termsOfUseHandler={navigateToTermsOfUse}
                isTransitTrip={activeItineraryIsTransit}
              />
              <NotificationOverlay
                visible={notificationVisible}
                message={intl.formatMessage({ id: 'planner.urlcopied' })}
              />
            </TripContentContainer>
          </>
        }
        hasInverseBackground={!skeletonMode}
      />
    </>
  );
}

export default TripPlanContainer;
